import { useEffect } from "react";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

interface PinKeypadProps {
  value: string;
  onDigit: (digit: string) => void;
  onBackspace: () => void;
  onSubmit?: () => void;
  showDigits: boolean;
  onToggleShowDigits: () => void;
  maxLength?: number;
  disabled?: boolean;
}

/**
 * Presentational numeric keypad: renders the masked/unmasked PIN display and
 * the digit grid, and wires up desktop physical-keyboard input. Has no
 * knowledge of HTTP/auth/routing — purely reports digit/backspace/submit
 * intents via props so it can be reused by both PinLoginPage and
 * ChangeAccessCodePage.
 */
const PinKeypad = ({
  value,
  onDigit,
  onBackspace,
  onSubmit,
  showDigits,
  onToggleShowDigits,
  maxLength = 10,
  disabled = false,
}: PinKeypadProps) => {
  const onClickDigit = (event: React.MouseEvent<HTMLButtonElement>) => {
    const digit = event.currentTarget.dataset.digit;
    if (digit && value.length < maxLength) {
      onDigit(digit);
    }
  };

  // Handle keyboard number input (desktop only)
  useEffect(() => {
    const isDesktop = window.innerWidth > 768;
    if (!isDesktop || disabled) return;

    const handleKeydown = (e: KeyboardEvent) => {
      const key = e.key;

      if (key >= '0' && key <= '9') {
        e.preventDefault();
        if (value.length < maxLength) {
          onDigit(key);
        }
      } else if (key === 'Backspace') {
        e.preventDefault();
        onBackspace();
      } else if (key === 'Enter' && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [value, maxLength, disabled, onDigit, onBackspace, onSubmit]);

  return (
    <>
      <div className="pad-display fs-2 text-wrap mb-4 p-2 w-100 rounded-3 text-center d-flex align-items-center justify-content-center fw-bold">
        {showDigits ? value : "•".repeat(value.length)}
      </div>

      <div className="row row-pin mb-2">
        <button className="btn-pin btn-pin-digit mx-2" data-digit="1" onClick={onClickDigit} disabled={disabled}>1</button>
        <button className="btn-pin btn-pin-digit mx-2" data-digit="2" onClick={onClickDigit} disabled={disabled}>2</button>
        <button className="btn-pin btn-pin-digit mx-2" data-digit="3" onClick={onClickDigit} disabled={disabled}>3</button>
      </div>
      <div className="row row-pin mb-2">
        <button className="btn-pin btn-pin-digit mx-2" data-digit="4" onClick={onClickDigit} disabled={disabled}>4</button>
        <button className="btn-pin btn-pin-digit mx-2" data-digit="5" onClick={onClickDigit} disabled={disabled}>5</button>
        <button className="btn-pin btn-pin-digit mx-2" data-digit="6" onClick={onClickDigit} disabled={disabled}>6</button>
      </div>
      <div className="row row-pin mb-2">
        <button className="btn-pin btn-pin-digit mx-2" data-digit="7" onClick={onClickDigit} disabled={disabled}>7</button>
        <button className="btn-pin btn-pin-digit mx-2" data-digit="8" onClick={onClickDigit} disabled={disabled}>8</button>
        <button className="btn-pin btn-pin-digit mx-2" data-digit="9" onClick={onClickDigit} disabled={disabled}>9</button>
      </div>
      <div className="row row-pin mb-2">
        <button className="btn-pin btn-pin-utility mx-2" onClick={onToggleShowDigits} disabled={disabled}>
          {showDigits ? <Eye size={20} /> : <EyeOff size={20} />}
        </button>
        <button className="btn-pin btn-pin-digit mx-2" data-digit="0" onClick={onClickDigit} disabled={disabled}>0</button>
        <button className="btn-pin btn-pin-utility mx-2" onClick={onBackspace} disabled={disabled}>
          <ArrowLeft size={20} />
        </button>
      </div>
    </>
  );
};

export default PinKeypad;
